import {
  type GetRepositoriesActionProps,
  type GetRepositoriesActionResponse
} from './types'
import useGetRepositories from 'api/hooks/useGetRepositories'
import useProblemDetails from 'hooks/useProblemDetails'
import {
  type ProblemDetailRegistry,
  type TranslationKeys
} from 'hooks/useProblemDetails/types'
import { Toaster } from '@matillion/component-library'
import { useEffect } from 'react'
import { Problem } from 'hooks/useProblemDetails/Problem'
import useGetProviderDisplayName from 'modules/BringYourOwnGit/hooks/useGetProviderDisplayName'

const commonFailureTitleKey = 'repositories.problem.default.title'

const problemRegistry: ProblemDetailRegistry = [
  {
    type: Problem.GIT_PROVIDER_AUTH_FAILED,
    translation: {
      titleKey: 'repositories.problem.auth-failed.title',
      messageKey: 'repositories.problem.auth-failed.message'
    }
  }
]

const defaultTranslationKey: TranslationKeys = {
  titleKey: commonFailureTitleKey,
  messageKey: 'repositories.problem.default.message'
}

const useGetRepositoriesAction = ({
  provider,
  onAuthRequired
}: GetRepositoriesActionProps): GetRepositoriesActionResponse => {
  const { data, isLoading, isError, error, fetchStatus, refetch } =
    useGetRepositories({ provider })

  const { makeToast } = Toaster.useToaster()
  const { providerName } = useGetProviderDisplayName(provider)

  const { resolveProblemDetails, isProblemType } = useProblemDetails(
    problemRegistry,
    [
      {
        name: 'provider',
        value: providerName
      }
    ]
  )

  useEffect(() => {
    if (isError) {
      const authRequired = isProblemType(
        error,
        Problem.GIT_PROVIDER_AUTH_REQUIRED
      )
      const authFailed = isProblemType(error, Problem.GIT_PROVIDER_AUTH_FAILED)

      if (authRequired || authFailed) {
        onAuthRequired()
      } else {
        const { title, message } = resolveProblemDetails(
          error,
          defaultTranslationKey
        )
        makeToast({ type: 'error', title, message })
      }
    }
  }, [
    isError,
    error,
    onAuthRequired,
    makeToast,
    resolveProblemDetails,
    isProblemType
  ])

  return {
    refetch,
    repositories: data?.repositories,
    loading: isLoading && fetchStatus !== 'idle'
  }
}

export default useGetRepositoriesAction
